import { L } from 'harmony-language';
import React, { FC, ReactNode } from 'react';
import type { LocationGroup, OrganizationLocation, Stop } from '../../../../../types';
import { toSiteDisplayNameFromOrganizationLocation, toSublocationDisplay } from '../../../../../utils/data-mapping';
import { AgisticsTooltip } from '../../../../shared/agistics-tooltip';
import { getSingleColorTagStyle } from '../../../utils/drafts-loads-utils';

interface AddressNotesTooltipContentProps {
    organizationLocation: OrganizationLocation;
    locationGroupName: string | undefined;
    sublocationDisplay: string | null;
    stopsNotes: ReactNode[];
    includeStopNotes: boolean;
}

const AddressNotesTooltipContent: React.FC<AddressNotesTooltipContentProps> = (props) => {
    const { organizationLocation, locationGroupName, sublocationDisplay, stopsNotes, includeStopNotes } = props;

    return (
        <div>
            <div>{L.address()}</div>
            <div>{L.localeAddressLine1(organizationLocation)}</div>
            {organizationLocation.address2 && organizationLocation.address2.trim().length ? <div>{L.localeAddressLine2(organizationLocation)}</div> : <></>}
            <div>{L.localeAddressLine3(organizationLocation)}</div>
            {locationGroupName && <><hr /><div>{locationGroupName}</div></>}
            {sublocationDisplay &&
                <>
                    <hr />
                    <div>{L.subLocations()}</div>
                    <div>{sublocationDisplay}</div>
                </>
            }
            {stopsNotes.length
                ? <><hr /><div>{stopsNotes}</div></>
                : includeStopNotes ? <><hr />{L.noNotes()}</> : null
            }
        </div>
    );
};

interface LoadStopLocationProps {
    organizationLocationId: number;
    organizationLocations: OrganizationLocation[];
    locationGroups: LocationGroup[];
    stops: Stop[];
    includeStopNotes?: boolean;
}

export const LoadStopLocation: FC<LoadStopLocationProps> = (props) => {
    const { organizationLocationId, organizationLocations, locationGroups, stops, includeStopNotes = true } = props;

    const organizationLocation = organizationLocations.find(x => x.id === organizationLocationId);

    if (organizationLocation) {
        const siteDisplayName = toSiteDisplayNameFromOrganizationLocation(organizationLocation);
        const stopsWithSublocations = stops.filter(stop => stop.organizationSubLocationIds && stop.organizationSubLocationIds.length);
        const sublocationDisplay = toSublocationDisplay(stopsWithSublocations, organizationLocation);
        const stopsNotes = stops.map(stop => {
            if (stop.notes) {
                return <div key={stop.id}>{stop.notes}</div>
            }
        }).filter(x => !!x);
        const locationGroupName = locationGroups.find(group => group.id === organizationLocation.groupId)?.name;
        const displayStyle = getSingleColorTagStyle(organizationLocation.groupId, locationGroups);

        return (
            <div className='lt-cell-sub'>
                <AgisticsTooltip
                    tooltip={
                        <AddressNotesTooltipContent
                            organizationLocation={organizationLocation}
                            sublocationDisplay={sublocationDisplay}
                            locationGroupName={locationGroupName}
                            stopsNotes={stopsNotes}
                            includeStopNotes={includeStopNotes}
                        />
                    }
                >
                    <div style={displayStyle}>{siteDisplayName}</div>
                </AgisticsTooltip>
            </div>
        );
    }
}
